import { useStore } from '../store';
import { useEffect, useState, useCallback, useRef } from 'react';
import { generateApi, exportApi, sessionsApi, templatesApi } from '../services/api';
import Sidebar from '../components/sidebar/Sidebar';
import Header from '../components/Header';
import SectionCard from '../components/SectionCard';
import SectionHeader from '../components/SectionHeader';
import SectionContent from '../components/SectionContent';
import SessionSidebar from '../components/SessionSidebar';
import ChatWidget from '../components/ChatWidget';
import DocumentPreviewModal from '../components/DocumentPreviewModal';
import SelectiveRegenerateModal from '../components/SelectiveRegenerateModal';
import TemplateManagerModal from '../components/TemplateManagerModal';
import GenericDocumentTemplateModal from '../components/GenericDocumentTemplateModal';
import HelpAssistant from '../components/HelpAssistant';
import ExportModal from '../components/ExportModal';
import EditableTitle from '../components/EditableTitle';
import ChainSettingsPanel from '../components/ChainSettingsPanel';
import ChainProgressBar from '../components/ChainProgressBar';
import ImageGenerationModal from '../components/ImageGenerationModal';
import CoverDesigner from '../components/CoverDesigner';
import StyleLibrary from '../components/StyleLibrary';
import PublishPanel from '../components/PublishPanel';
import ImageBlock from '../components/ImageBlock';
import LayoutRenderer from '../components/LayoutRenderer';
import ProjectWizard from '../components/ProjectWizard';
import DocumentTypeIndicator from '../components/DocumentTypeIndicator';
import type { ChainProgressEvent, DocumentConfig } from '../types';
import { getDocumentTypeById, getPageFormatById } from '../data/document-presets';
import { imageApi } from '../services/imageApi';
import { IMAGE_PROVIDERS, ImageProvider } from '../types/images';
import {
  Plus,
  FileText,
  Sparkles,
  Eye,
  FileDown,
  FileOutput,
  Loader2,
  Save,
  Link2,
  BookOpen,
  Palette,
  Download,
  File,
} from 'lucide-react';

export default function Dashboard() {
  const {
    // State
    sections,
    applicantData,
    genericSections,
    currentSessionName,
    hasUnsavedChanges,
    isSidebarOpen,
    currentLetterTemplateId,
    selectedDocuments,
    documentConfig,
    showProjectWizard,
    pageViewZoom,
    pageViewMode,
    allTemplates,
    documentTemplates,
    systemPrompt,
    chainConfig,
    nuanceSettings,
    isChainGenerating,
    referenceText,
    styleOptions,
    imageBlocks,
    currentSessionId,

    // Actions
    addSection,
    updateSection,
    setSections,
    updateSessionName,
    saveCurrentSession,
    createNewSession,
    toggleSidebar,
    toggleSidebarCollapse,
    toggleSessionSidebar,
    setSessions,
    setAllTemplates,
    setDocumentTemplates,
    addNotification,
    setChainProgress,
    setChainGenerating,
    addImageBlock,
    setImageBlocks,
    setStyleReferences,
    setDocumentConfig,
    setShowProjectWizard,
    setPageViewZoom,
    setPageViewMode,
    // Story mode
    setStoryOutline,
    setStoryBible,
  } = useStore();

  // Modal visibility states
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showSelectiveRegenModal, setShowSelectiveRegenModal] = useState(false);
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [showGenericDocTemplateModal, setShowGenericDocTemplateModal] = useState(false);
  const [showHelpAssistant, setShowHelpAssistant] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImageGenerator, setShowImageGenerator] = useState(false);
  const [showCoverDesigner, setShowCoverDesigner] = useState(false);
  const [showStyleLibrary, setShowStyleLibrary] = useState(false);
  const [showPublishPanel, setShowPublishPanel] = useState(false);
  const [imageGenSectionId, setImageGenSectionId] = useState<number | null>(null);

  // Generation/export loading states
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isExportingDOCX, setIsExportingDOCX] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Selective regeneration context
  const [selectiveRegenContext, setSelectiveRegenContext] = useState<{
    sectionId: number;
    sectionTitle: string;
    fullContent: string;
    selectedText: string;
    startIndex: number;
    endIndex: number;
  } | null>(null);

  // Ref to track mounted state
  const isMountedRef = useRef(true);

  // Chain generation abort controller
  const chainAbortRef = useRef<AbortController | null>(null);

  // Helper: Get image dimensions based on size setting
  const getImageDimensions = useCallback((size: 'square' | 'portrait' | 'landscape', provider: ImageProvider) => {
    const providerConfig = IMAGE_PROVIDERS[provider];
    const sizes = providerConfig?.supportedSizes || [];
    switch (size) {
      case 'portrait':
        return sizes.find(s => s.height > s.width) || { width: 1024, height: 1792 };
      case 'landscape':
        return sizes.find(s => s.width > s.height) || { width: 1792, height: 1024 };
      case 'square':
      default:
        return sizes.find(s => s.width === s.height) || { width: 1024, height: 1024 };
    }
  }, []);

  // Helper: Generate image prompt from content
  const generateImagePrompt = useCallback((content: string, title: string): string => {
    const firstSentence = content.split(/[.!?]/)[0]?.trim() || '';
    const context = firstSentence.length > 100 ? firstSentence.substring(0, 100) : firstSentence;
    return `Professional illustration for a section titled "${title}". Context: ${context}. Style: clean, modern, artistic.`;
  }, []);

  // Helper: Generate image for a section (used by chain generation)
  const generateImageForSection = useCallback(async (section: typeof sections[0], content: string) => {
    if (!section.imageConfig?.enabled) return;

    try {
      updateSection(section.id, { isGeneratingImage: true });

      const imagePrompt = section.imageConfig.prompt?.trim() || generateImagePrompt(content, section.title);
      const dimensions = getImageDimensions(
        section.imageConfig.size || 'square',
        section.imageConfig.provider || 'openai'
      );

      const imageResult = await imageApi.generate({
        prompt: imagePrompt,
        provider: section.imageConfig.provider || 'openai',
        width: dimensions.width,
        height: dimensions.height,
      });

      if (imageResult.success && imageResult.image) {
        const imageBlock = {
          id: `chain-${section.id}-${Date.now()}`,
          sectionId: section.id,
          position: 'inline' as const,
          imageUrl: imageResult.image.url,
          width: 'medium' as const,
          alignment: 'center' as const,
          generatedPrompt: imagePrompt,
          provider: section.imageConfig.provider,
        };
        addImageBlock(section.id, imageBlock);
      }
    } catch (err: any) {
      console.error(`Image generation failed for section ${section.id}:`, err);
      addNotification(`Image failed for "${section.title}"`, 'error');
    } finally {
      updateSection(section.id, { isGeneratingImage: false });
    }
  }, [updateSection, addImageBlock, addNotification, getImageDimensions, generateImagePrompt]);

  // ----------------------------------------------------------
  // Fetch sessions and templates on mount
  // ----------------------------------------------------------
  const fetchSessions = useCallback(async () => {
    try {
      const data = await sessionsApi.getAll();
      if (isMountedRef.current) {
        setSessions(data);
      }
    } catch {
      // Sessions may not be available if user is new
    }
  }, [setSessions]);

  const fetchTemplates = useCallback(async () => {
    try {
      const [templates, docTemplates] = await Promise.all([
        templatesApi.getAll(),
        templatesApi.getAllDocumentTemplates(),
      ]);
      if (isMountedRef.current) {
        setAllTemplates(templates);
        setDocumentTemplates(docTemplates);
      }
    } catch {
      // Templates may not be available yet
    }
  }, [setAllTemplates, setDocumentTemplates]);

  // Fetch style references
  const fetchStyleReferences = useCallback(async () => {
    try {
      const result = await imageApi.getStyleReferences();
      if (isMountedRef.current) {
        setStyleReferences(result.references);
      }
    } catch {
      // Style references may not be available
    }
  }, [setStyleReferences]);

  // Fetch image blocks for current session
  const fetchImageBlocks = useCallback(async () => {
    if (!currentSessionId) return;
    try {
      const result = await imageApi.getImageBlocks(currentSessionId);
      if (isMountedRef.current) {
        // Convert array to record keyed by sectionId
        const blocksRecord: Record<number, any[]> = {};
        for (const block of result.blocks) {
          if (!blocksRecord[block.sectionId]) {
            blocksRecord[block.sectionId] = [];
          }
          blocksRecord[block.sectionId].push(block);
        }
        setImageBlocks(blocksRecord);
      }
    } catch {
      // Image blocks may not be available
    }
  }, [currentSessionId, setImageBlocks]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchSessions();
    fetchTemplates();
    fetchStyleReferences();
    fetchImageBlocks();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchSessions, fetchTemplates, fetchStyleReferences, fetchImageBlocks]);

  // ----------------------------------------------------------
  // Keyboard shortcuts
  // ----------------------------------------------------------
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isCtrl = e.ctrlKey || e.metaKey;

      // Ctrl+S - Save
      if (isCtrl && !e.shiftKey && e.key === 's') {
        e.preventDefault();
        handleSave();
        return;
      }

      // Ctrl+Shift+S - Save as new
      if (isCtrl && e.shiftKey && e.key === 'S') {
        e.preventDefault();
        handleSaveAsNew();
        return;
      }

      // Ctrl+N - New session
      if (isCtrl && e.key === 'n') {
        e.preventDefault();
        handleNewSession();
        return;
      }

      // Ctrl+O - Toggle session sidebar
      if (isCtrl && e.key === 'o') {
        e.preventDefault();
        toggleSessionSidebar();
        return;
      }

      // Ctrl+B - Toggle sidebar collapse
      if (isCtrl && e.key === 'b') {
        e.preventDefault();
        if (isSidebarOpen) {
          toggleSidebarCollapse();
        } else {
          toggleSidebar();
        }
        return;
      }

      // Escape - Close modals
      if (e.key === 'Escape') {
        if (showProjectWizard) {
          setShowProjectWizard(false);
          return;
        }
        if (showPreviewModal) {
          setShowPreviewModal(false);
          return;
        }
        if (showSelectiveRegenModal) {
          setShowSelectiveRegenModal(false);
          setSelectiveRegenContext(null);
          return;
        }
        if (showTemplateManager) {
          setShowTemplateManager(false);
          return;
        }
        if (showGenericDocTemplateModal) {
          setShowGenericDocTemplateModal(false);
          return;
        }
        if (showHelpAssistant) {
          setShowHelpAssistant(false);
          return;
        }
        if (showImageGenerator) {
          setShowImageGenerator(false);
          setImageGenSectionId(null);
          return;
        }
        if (showCoverDesigner) {
          setShowCoverDesigner(false);
          return;
        }
        if (showStyleLibrary) {
          setShowStyleLibrary(false);
          return;
        }
        if (showPublishPanel) {
          setShowPublishPanel(false);
          return;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isSidebarOpen,
    showProjectWizard,
    showPreviewModal,
    showSelectiveRegenModal,
    showTemplateManager,
    showGenericDocTemplateModal,
    showHelpAssistant,
    showImageGenerator,
    showCoverDesigner,
    showStyleLibrary,
    showPublishPanel,
    toggleSidebar,
    toggleSidebarCollapse,
    toggleSessionSidebar,
    setShowProjectWizard,
  ]);

  // Listen for help assistant events from Settings panel
  useEffect(() => {
    const handleOpenHelp = () => {
      setShowHelpAssistant(true);
    };
    window.addEventListener('open-help-assistant', handleOpenHelp);
    return () => window.removeEventListener('open-help-assistant', handleOpenHelp);
  }, []);

  // ----------------------------------------------------------
  // Build generic fields helper
  // ----------------------------------------------------------
  const buildGenericFields = useCallback((): Record<string, Record<string, string>> => {
    const genericFields: Record<string, Record<string, string>> = {};
    const titleCount: Record<string, number> = {};
    genericSections.forEach((gs) => {
      // Handle duplicate section titles by appending counter
      let sectionKey = gs.title;
      titleCount[gs.title] = (titleCount[gs.title] || 0) + 1;
      if (titleCount[gs.title] > 1) {
        sectionKey = `${gs.title} (${titleCount[gs.title]})`;
      }
      genericFields[sectionKey] = {};
      const labelCount: Record<string, number> = {};
      gs.fields.forEach((field) => {
        if (field.value) {
          let fieldKey = field.label;
          labelCount[field.label] = (labelCount[field.label] || 0) + 1;
          if (labelCount[field.label] > 1) {
            fieldKey = `${field.label} (${labelCount[field.label]})`;
          }
          genericFields[sectionKey][fieldKey] = field.value;
        }
      });
    });
    return genericFields;
  }, [genericSections]);

  // ----------------------------------------------------------
  // Generate a single section
  // ----------------------------------------------------------
  const generateSection = useCallback(
    async (sectionId: number) => {
      const section = sections.find((s) => s.id === sectionId);
      if (!section) return;

      updateSection(sectionId, { isGenerating: true });

      try {
        const genericFields = buildGenericFields();

        const result = await generateApi.generate({
          section: {
            id: section.id,
            title: section.title,
            prompt: section.prompt,
            documents: section.documents,
            role: section.role,
          },
          applicantData: {
            ...applicantData,
            genericFields,
          },
          selectedDocuments,
          systemPrompt: systemPrompt || undefined,
          llmModel: applicantData.llmModel,
          nuanceSettings,
          referenceText: referenceText || undefined,
          styleOptions: referenceText ? styleOptions : undefined,
        });

        if (result.success) {
          updateSection(sectionId, { content: result.content });
          addNotification(`Generated: ${section.title}`, 'success');
        } else {
          addNotification(result.error || `Failed to generate: ${section.title}`, 'error');
        }
      } catch (err: any) {
        addNotification(
          err.response?.data?.error || `Generation failed: ${section.title}`,
          'error'
        );
      } finally {
        updateSection(sectionId, { isGenerating: false });
      }
    },
    [sections, applicantData, genericSections, selectedDocuments, systemPrompt, nuanceSettings, referenceText, styleOptions, updateSection, addNotification, buildGenericFields]
  );

  // ----------------------------------------------------------
  // Generate all sections
  // ----------------------------------------------------------
  const generateAllSections = useCallback(async () => {
    if (sections.length === 0) {
      addNotification('No sections to generate', 'error');
      return;
    }

    setIsGeneratingAll(true);
    const genericFields = buildGenericFields();

    let successCount = 0;
    let failCount = 0;

    for (const section of sections) {
      updateSection(section.id, { isGenerating: true });

      try {
        const result = await generateApi.generate({
          section: {
            id: section.id,
            title: section.title,
            prompt: section.prompt,
            documents: section.documents,
            role: section.role,
          },
          applicantData: {
            ...applicantData,
            genericFields,
          },
          selectedDocuments,
          systemPrompt: systemPrompt || undefined,
          llmModel: applicantData.llmModel,
          nuanceSettings,
          referenceText: referenceText || undefined,
          styleOptions: referenceText ? styleOptions : undefined,
        });

        if (result.success) {
          updateSection(section.id, { content: result.content });
          successCount++;
        } else {
          failCount++;
        }
      } catch {
        failCount++;
      } finally {
        updateSection(section.id, { isGenerating: false });
      }
    }

    setIsGeneratingAll(false);

    if (failCount === 0) {
      addNotification(`All ${successCount} section(s) generated successfully`, 'success');
    } else {
      addNotification(
        `Generated ${successCount} section(s), ${failCount} failed`,
        failCount > successCount ? 'error' : 'info'
      );
    }
  }, [sections, applicantData, selectedDocuments, systemPrompt, nuanceSettings, referenceText, styleOptions, updateSection, addNotification, buildGenericFields]);

  // ----------------------------------------------------------
  // Chain generate all sections (SSE)
  // ----------------------------------------------------------
  const chainGenerateAllSections = useCallback(() => {
    if (sections.length === 0) {
      addNotification('No sections to generate', 'error');
      return;
    }

    setChainGenerating(true);
    setChainProgress({ type: 'start', totalSections: sections.length });

    // Mark all sections as generating
    sections.forEach((s) => updateSection(s.id, { isGenerating: true }));

    const genericFields = buildGenericFields();

    // Build document contents from selected documents
    const documentContents = (selectedDocuments || [])
      .filter((d: any) => d.content)
      .map((d: any) => ({ filename: d.filename || d.name || 'Document', content: d.content }));

    const controller = generateApi.chain(
      {
        sections: sections.map((s) => ({
          id: s.id,
          title: s.title,
          prompt: s.prompt,
          role: s.role,
          documents: s.documents,
        })),
        applicantData: {
          ...applicantData,
          genericFields,
        },
        chainConfig: {
          contextDepth: chainConfig.contextDepth,
          contextFormat: chainConfig.contextFormat,
          continueFlow: chainConfig.continueFlow,
          // Story mode options
          storyMode: chainConfig.storyMode,
          generateOutline: chainConfig.generateOutline,
          trackCharacters: chainConfig.trackCharacters,
          creativityLevel: chainConfig.creativityLevel,
        },
        nuanceSettings,
        systemPrompt: systemPrompt || undefined,
        llmModel: applicantData.llmModel || undefined,
        referenceText: referenceText || undefined,
        styleOptions: referenceText ? styleOptions : undefined,
        documentContents: documentContents.length > 0 ? documentContents : undefined,
      },
      (event: ChainProgressEvent) => {
        if (!isMountedRef.current) return;

        setChainProgress(event);

        // Story mode: planning phase
        if (event.type === 'planning') {
          addNotification('Planning story outline...', 'info');
        }

        if (event.type === 'planning_complete' && event.outline) {
          setStoryOutline(event.outline);
          addNotification('Story outline created', 'success');
        }

        // Story mode: bible updates
        if (event.type === 'bible_update' && event.bible) {
          setStoryBible(event.bible);
        }

        if (event.type === 'section_start' && event.sectionId !== undefined) {
          updateSection(event.sectionId, { isGenerating: true });
        }

        if (event.type === 'section_complete' && event.sectionId !== undefined) {
          const content = event.content || '';
          updateSection(event.sectionId, {
            content,
            isGenerating: false,
          });

          // Check if this section has image generation enabled
          const completedSection = sections.find(s => s.id === event.sectionId);
          if (completedSection?.imageConfig?.enabled && content) {
            // Fire off image generation (don't block the chain)
            generateImageForSection(completedSection, content);
          }
        }

        if (event.type === 'section_error' && event.sectionId !== undefined) {
          updateSection(event.sectionId, { isGenerating: false });
          addNotification(`Failed: ${event.sectionTitle || 'section'} - ${event.error}`, 'error');
        }

        if (event.type === 'complete') {
          setChainGenerating(false);
          sections.forEach((s) => updateSection(s.id, { isGenerating: false }));
          // Store final outline and bible if provided
          if (event.outline) setStoryOutline(event.outline);
          if (event.bible) setStoryBible(event.bible);
          addNotification('Chain generation complete', 'success');
          // Clear progress after a brief delay
          setTimeout(() => {
            if (isMountedRef.current) setChainProgress(null);
          }, 2000);
        }
      },
      (error: string) => {
        if (!isMountedRef.current) return;
        setChainGenerating(false);
        setChainProgress(null);
        sections.forEach((s) => updateSection(s.id, { isGenerating: false }));
        addNotification(`Chain generation error: ${error}`, 'error');
      }
    );

    chainAbortRef.current = controller;
  }, [sections, applicantData, selectedDocuments, systemPrompt, chainConfig, nuanceSettings, updateSection, addNotification, buildGenericFields, setChainGenerating, setChainProgress, generateImageForSection, setStoryOutline, setStoryBible]);

  // ----------------------------------------------------------
  // Cancel chain generation
  // ----------------------------------------------------------
  const cancelChainGeneration = useCallback(() => {
    if (chainAbortRef.current) {
      chainAbortRef.current.abort();
      chainAbortRef.current = null;
    }
    setChainGenerating(false);
    setChainProgress(null);
    sections.forEach((s) => updateSection(s.id, { isGenerating: false }));
    addNotification('Chain generation cancelled', 'info');
  }, [sections, updateSection, addNotification, setChainGenerating, setChainProgress]);

  // ----------------------------------------------------------
  // Export helpers
  // ----------------------------------------------------------
  const buildExportApplicantData = useCallback(() => {
    const exportData = { ...applicantData };
    genericSections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.id === 'primary_name' && field.value) {
          exportData.beneficiaryName = field.value;
        }
        if (field.id === 'secondary_name' && field.value) {
          exportData.petitionerName = field.value;
        }
        if (field.id === 'reference_number' && field.value) {
          exportData.caseNumber = field.value;
        }
      });
    });
    return exportData;
  }, [applicantData, genericSections]);

  const triggerDownload = useCallback((blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }, []);

  // ----------------------------------------------------------
  // Export PDF
  // ----------------------------------------------------------
  const handleExportPDF = useCallback(async () => {
    if (sections.length === 0) {
      addNotification('No content to export', 'error');
      return;
    }

    setIsExportingPDF(true);
    try {
      const blob = await exportApi.export({
        format: 'pdf',
        applicantData: buildExportApplicantData(),
        sections: sections.map((s) => ({ title: s.title, content: s.content })),
        documentTitle: currentSessionName,
      });

      triggerDownload(blob, `${currentSessionName.replace(/\s+/g, '_')}.pdf`);
      addNotification('PDF exported successfully', 'success');
    } catch (err: any) {
      addNotification(err.response?.data?.error || 'PDF export failed', 'error');
    } finally {
      setIsExportingPDF(false);
    }
  }, [sections, currentSessionName, addNotification, buildExportApplicantData, triggerDownload]);

  // ----------------------------------------------------------
  // Export DOCX
  // ----------------------------------------------------------
  const handleExportDOCX = useCallback(async () => {
    if (sections.length === 0) {
      addNotification('No content to export', 'error');
      return;
    }

    setIsExportingDOCX(true);
    try {
      const blob = await exportApi.export({
        format: 'docx',
        applicantData: buildExportApplicantData(),
        sections: sections.map((s) => ({ title: s.title, content: s.content })),
        documentTitle: currentSessionName,
      });

      triggerDownload(blob, `${currentSessionName.replace(/\s+/g, '_')}.docx`);
      addNotification('DOCX exported successfully', 'success');
    } catch (err: any) {
      addNotification(err.response?.data?.error || 'DOCX export failed', 'error');
    } finally {
      setIsExportingDOCX(false);
    }
  }, [sections, currentSessionName, addNotification, buildExportApplicantData, triggerDownload]);

  // ----------------------------------------------------------
  // Selective Regenerate
  // ----------------------------------------------------------
  const handleSelectiveRegenerate = useCallback(
    async (instructions: string) => {
      if (!selectiveRegenContext) return;

      const { sectionId, sectionTitle, fullContent, selectedText, startIndex, endIndex } = selectiveRegenContext;

      updateSection(sectionId, { isGenerating: true });
      setShowSelectiveRegenModal(false);

      try {
        const result = await generateApi.regenerateSelection({
          fullContent,
          selectedText,
          instructions,
          startIndex,
          endIndex,
          sectionTitle,
          applicantData: {
            ...applicantData,
            genericFields: buildGenericFields(),
          },
          llmModel: applicantData.llmModel,
        });

        if (result.success) {
          // Replace the selected portion with the regenerated text
          const newContent =
            fullContent.substring(0, startIndex) +
            result.content +
            fullContent.substring(endIndex);
          updateSection(sectionId, { content: newContent });
          addNotification('Selection regenerated', 'success');
        } else {
          addNotification(result.error || 'Regeneration failed', 'error');
        }
      } catch (err: any) {
        addNotification(
          err.response?.data?.error || 'Selective regeneration failed',
          'error'
        );
      } finally {
        updateSection(sectionId, { isGenerating: false });
        setSelectiveRegenContext(null);
      }
    },
    [selectiveRegenContext, applicantData, updateSection, addNotification, buildGenericFields]
  );

  // ----------------------------------------------------------
  // Open Selective Regenerate Modal (from SectionCard onRegenerate)
  // ----------------------------------------------------------
  const openSelectiveRegenerate = useCallback(
    (sectionId: number, selectedText: string, startIndex: number, endIndex: number) => {
      const section = sections.find((s) => s.id === sectionId);
      if (!section) return;

      setSelectiveRegenContext({
        sectionId,
        sectionTitle: section.title,
        fullContent: section.content,
        selectedText,
        startIndex,
        endIndex,
      });
      setShowSelectiveRegenModal(true);
    },
    [sections]
  );

  // ----------------------------------------------------------
  // Section reorder helpers
  // ----------------------------------------------------------
  const handleMoveSection = useCallback(
    (sectionId: number, direction: 'up' | 'down') => {
      const currentSections = [...sections];
      const index = currentSections.findIndex((s) => s.id === sectionId);
      if (index < 0) return;

      if (direction === 'up' && index > 0) {
        [currentSections[index - 1], currentSections[index]] = [currentSections[index], currentSections[index - 1]];
        setSections(currentSections);
      } else if (direction === 'down' && index < currentSections.length - 1) {
        [currentSections[index], currentSections[index + 1]] = [currentSections[index + 1], currentSections[index]];
        setSections(currentSections);
      }
    },
    [sections, setSections]
  );

  // ----------------------------------------------------------
  // Save
  // ----------------------------------------------------------
  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await saveCurrentSession();
    } catch {
      addNotification('Failed to save session', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [saveCurrentSession, addNotification]);

  // ----------------------------------------------------------
  // Save As New
  // ----------------------------------------------------------
  const handleSaveAsNew = useCallback(async () => {
    setIsSaving(true);
    try {
      const sessionState = {
        applicantData,
        sections,
        genericSections,
        systemPrompt,
        documentType: useStore.getState().documentType,
        currentLetterTemplateId: useStore.getState().currentLetterTemplateId,
        selectedDocuments,
        allTemplates,
        documentTemplates,
      };

      const newSession = await sessionsApi.create({
        name: `${currentSessionName} (Copy)`,
        documentType: useStore.getState().documentType,
        state: sessionState,
      });

      useStore.setState({
        currentSessionId: newSession.id,
        currentSessionName: `${currentSessionName} (Copy)`,
        hasUnsavedChanges: false,
      });

      addNotification('Saved as new document', 'success');
      fetchSessions();
    } catch {
      addNotification('Failed to save as new', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [
    applicantData,
    sections,
    genericSections,
    systemPrompt,
    selectedDocuments,
    allTemplates,
    documentTemplates,
    currentSessionName,
    addNotification,
    fetchSessions,
  ]);


  // ----------------------------------------------------------
  // New Session (with unsaved changes warning)
  // ----------------------------------------------------------
  const handleNewSession = useCallback(() => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Do you want to discard them and start a new session?'
      );
      if (!confirmed) return;
    }

    createNewSession();
  }, [hasUnsavedChanges, createNewSession]);

  // ----------------------------------------------------------
  // Project Wizard Completion
  // ----------------------------------------------------------
  const handleWizardComplete = useCallback((config: DocumentConfig) => {
    setDocumentConfig(config);
    setShowProjectWizard(false);
    // Update session name from wizard title
    if (config.metadata.title) {
      updateSessionName(config.metadata.title);
    }
    addNotification(`Project created: ${config.metadata.title}`, 'success');
  }, [setDocumentConfig, setShowProjectWizard, updateSessionName, addNotification]);

  // Get document type and page format for feature-based toolbar and page view
  const currentDocType = documentConfig ? getDocumentTypeById(documentConfig.documentType) : null;
  const currentPageFormat = documentConfig ? getPageFormatById(documentConfig.pageFormat) : null;

  // ----------------------------------------------------------
  // Preview
  // ----------------------------------------------------------
  const handlePreview = useCallback(() => {
    if (sections.length === 0) {
      addNotification('No content to preview', 'error');
      return;
    }
    setShowPreviewModal(true);
  }, [sections, addNotification]);

  // ----------------------------------------------------------
  // Image Generation for a section
  // ----------------------------------------------------------
  const handleAddImageToSection = useCallback((sectionId: number) => {
    setImageGenSectionId(sectionId);
    setShowImageGenerator(true);
  }, []);

  const handleImageSelect = useCallback(async (imageUrl: string, prompt: string) => {
    if (imageGenSectionId !== null) {
      if (!currentSessionId) {
        addNotification('No active session', 'error');
        setShowImageGenerator(false);
        setImageGenSectionId(null);
        return;
      }

      try {
        // Ensure session is saved before saving image blocks
        if (hasUnsavedChanges) {
          await saveCurrentSession();
        }

        // Save to backend
        const result = await imageApi.saveImageBlock(currentSessionId, {
          sectionId: imageGenSectionId,
          position: 'after',
          imageUrl,
          width: 'medium',
          alignment: 'center',
          generatedPrompt: prompt,
        });

        if (result.success) {
          addImageBlock(imageGenSectionId, result.block);
          addNotification('Image added to section', 'success');
        }
      } catch (err) {
        console.error('Failed to save image:', err);
        addNotification('Failed to save image', 'error');
      }
    }
    setShowImageGenerator(false);
    setImageGenSectionId(null);
  }, [imageGenSectionId, currentSessionId, hasUnsavedChanges, saveCurrentSession, addImageBlock, addNotification]);

  // ----------------------------------------------------------
  // Layout classes
  // ----------------------------------------------------------
  const mainContentClasses = 'flex-1';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <Header onSettingsClick={() => { if (!isSidebarOpen) toggleSidebar(); }} />

      <div className="flex pt-16">
        {/* Left Sidebar - Session History */}
        <SessionSidebar />

        {/* Main Content Area */}
        <main className={mainContentClasses}>
          <div className="p-6 pb-24">
            <div className="max-w-4xl mx-auto">
              {/* Editable Document Title */}
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <EditableTitle
                    value={currentSessionName}
                    onChange={updateSessionName}
                    placeholder="Document Title"
                  />
                  {hasUnsavedChanges && (
                    <span className="text-xs text-orange-500 whitespace-nowrap flex items-center gap-1">
                      <span className="w-2 h-2 bg-orange-400 rounded-full inline-block" />
                      Unsaved
                    </span>
                  )}
                </div>
                <DocumentTypeIndicator
                  onOpenWizard={() => setShowProjectWizard(true)}
                />
              </div>

              {/* Action Toolbar - right-aligned above sections */}
              <div className="mb-4 flex items-center justify-between gap-2">
                {/* Left: Visual Content Tools */}
                <div className="flex items-center gap-2">
                  {/* Style Library - Show when images are enabled or no config */}
                  {(!currentDocType || currentDocType.features.showImages) && (
                    <button
                      onClick={() => setShowStyleLibrary(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-accent bg-accent/10 border border-accent/30 rounded-lg hover:bg-accent/20 transition-colors whitespace-nowrap"
                      title="Style Library - manage reference images"
                    >
                      <Palette size={14} />
                      Styles
                    </button>
                  )}

                  {/* Cover Designer - Show when cover is enabled or no config */}
                  {(!currentDocType || currentDocType.features.showCover) && (
                    <button
                      onClick={() => setShowCoverDesigner(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-accent bg-accent/10 border border-accent/30 rounded-lg hover:bg-accent/20 transition-colors whitespace-nowrap"
                      title="Design book cover"
                    >
                      <BookOpen size={14} />
                      Cover
                    </button>
                  )}

                  {/* Publish */}
                  <button
                    onClick={() => setShowPublishPanel(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-accent bg-accent/10 border border-accent/30 rounded-lg hover:bg-accent/20 transition-colors whitespace-nowrap"
                    title="Publish to ePub, Kindle, or PDF"
                  >
                    <Download size={14} />
                    Publish
                  </button>
                </div>

                {/* Right: Document Actions */}
                <div className="flex items-center gap-2">
                  {/* Reset to Template */}
                  <button
                    onClick={() => {
                      if (window.confirm('Reset all sections to the current template? This will remove all custom sections.')) {
                        const currentTemplate = allTemplates.find((t) => t.id === currentLetterTemplateId);
                        if (currentTemplate) {
                          setSections(
                            currentTemplate.sections.map((s, i) => ({
                              ...s,
                              id: i + 1,
                              content: '',
                              isGenerating: false,
                              isEditing: false,
                              documents: [],
                            }))
                          );
                          addNotification('Sections reset to template', 'success');
                        } else {
                          addNotification('No template selected', 'info');
                        }
                      }
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
                    title="Reset sections to template"
                  >
                    Reset
                  </button>

                  {/* Clear All */}
                  {sections.some((s) => s.content) && (
                    <button
                      onClick={() => {
                        if (window.confirm('Clear all generated content from all sections?')) {
                          sections.forEach((s) => {
                            if (s.content) updateSection(s.id, { content: '' });
                          });
                          addNotification('All content cleared', 'info');
                        }
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-orange-600 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors whitespace-nowrap"
                      title="Clear all generated content"
                    >
                      Clear All
                    </button>
                  )}

                  {/* Save Session */}
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50 whitespace-nowrap"
                    title="Save (Ctrl+S)"
                  >
                    {isSaving ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Save size={14} />
                    )}
                    Save
                  </button>

                  {/* Export */}
                  <button
                    onClick={() => setShowExportModal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
                    title="Export document"
                  >
                    <FileDown size={14} />
                    Export
                  </button>
                </div>
              </div>

              {/* Chain Settings Panel */}
              <ChainSettingsPanel />

              {/* Chain Progress Bar */}
              <ChainProgressBar
                sections={sections}
                onCancel={cancelChainGeneration}
              />

              {/* Section Cards - With Page View when document config exists */}
              {sections.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl border border-gray-200 shadow-sm">
                  <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No sections yet
                  </h3>
                  <p className="text-gray-500 mb-6 max-w-sm mx-auto">
                    Select a template from the sidebar or add sections manually to get started.
                  </p>
                  <div className="flex items-center justify-center gap-3">
                    <button
                      onClick={() => addSection()}
                      className="btn btn-primary inline-flex items-center gap-2"
                    >
                      <Plus size={18} />
                      Add First Section
                    </button>
                    <button
                      onClick={() => setShowTemplateManager(true)}
                      className="btn btn-secondary inline-flex items-center gap-2"
                    >
                      <FileText size={18} />
                      Browse Templates
                    </button>
                  </div>
                </div>
              ) : currentPageFormat ? (
                /* Page View Mode - Shows actual page proportions */
                <div className="space-y-6">
                  {/* Zoom & View Controls */}
                  <div className="flex items-center justify-center gap-2 py-2 px-4 bg-gray-100 rounded-lg border border-gray-200">
                    <button
                      onClick={() => setPageViewZoom(Math.max(0.5, pageViewZoom - 0.25))}
                      disabled={pageViewZoom <= 0.5}
                      className="px-2 py-1 text-sm text-gray-600 hover:bg-gray-200 rounded disabled:opacity-40"
                    >
                      -
                    </button>
                    <span className="px-3 text-sm font-medium text-gray-700 min-w-[60px] text-center">
                      {Math.round(pageViewZoom * 100)}%
                    </span>
                    <button
                      onClick={() => setPageViewZoom(Math.min(2, pageViewZoom + 0.25))}
                      disabled={pageViewZoom >= 2}
                      className="px-2 py-1 text-sm text-gray-600 hover:bg-gray-200 rounded disabled:opacity-40"
                    >
                      +
                    </button>
                    <div className="h-4 w-px bg-gray-300 mx-2" />
                    {/* View Mode Toggle */}
                    <div className="flex items-center bg-white rounded border border-gray-200">
                      <button
                        onClick={() => setPageViewMode('single')}
                        className={`p-1.5 rounded-l transition-colors ${
                          pageViewMode === 'single'
                            ? 'bg-accent text-white'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                        title="Single page view"
                      >
                        <File size={16} />
                      </button>
                      <button
                        onClick={() => setPageViewMode('twoPage')}
                        className={`p-1.5 rounded-r transition-colors ${
                          pageViewMode === 'twoPage'
                            ? 'bg-accent text-white'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                        title="Two-page spread view"
                      >
                        <BookOpen size={16} />
                      </button>
                    </div>
                    <div className="h-4 w-px bg-gray-300 mx-2" />
                    <span className="text-xs text-gray-500">
                      {currentPageFormat.shortName} ({currentPageFormat.widthInches}" x {currentPageFormat.heightInches}")
                    </span>
                  </div>

                  {/* Pages Container */}
                  <div
                    className="rounded-lg p-8"
                    style={{ backgroundColor: '#6b7280' }}
                  >
                    {pageViewMode === 'single' ? (
                      /* Single Page View */
                      <div className="flex flex-col items-center gap-8">
                        {sections.map((section, index) => {
                          const pageWidth = currentPageFormat.widthInches * 96 * pageViewZoom;
                          const pageHeight = currentPageFormat.heightInches * 96 * pageViewZoom;

                          return (
                            <div key={section.id} className="flex flex-col items-center gap-2">
                              {/* Section Header - Outside the page */}
                              <div style={{ width: `${pageWidth}px` }}>
                                <SectionHeader
                                  section={section}
                                  sectionNumber={index + 1}
                                  isFirst={index === 0}
                                  isLast={index === sections.length - 1}
                                  onGenerate={() => generateSection(section.id)}
                                  onMoveUp={() => handleMoveSection(section.id, 'up')}
                                  onMoveDown={() => handleMoveSection(section.id, 'down')}
                                  onAddAfter={() => addSection(section.id)}
                                                                  />
                              </div>

                              {/* Page - Only content inside */}
                              <div
                                className="bg-white relative transition-all duration-200 rounded-sm"
                                style={{
                                  width: `${pageWidth}px`,
                                  minHeight: `${pageHeight}px`,
                                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.2)',
                                }}
                              >
                                <LayoutRenderer
                                  layout={section.layout || 'full-width'}
                                  sectionId={section.id}
                                  images={imageBlocks[section.id] || []}
                                  onImageEdit={() => handleAddImageToSection(section.id)}
                                  content={
                                    <SectionContent
                                      section={section}
                                      onRegenerate={(selectedText, startIndex, endIndex) =>
                                        openSelectiveRegenerate(section.id, selectedText, startIndex, endIndex)
                                      }
                                    />
                                  }
                                />

                                {/* Page number indicator */}
                                <div
                                  className="absolute bottom-2 left-1/2 transform -translate-x-1/2 text-gray-400"
                                  style={{ fontSize: `${12 * pageViewZoom}px` }}
                                >
                                  Page {index + 1}
                                </div>
                              </div>

                              {/* Page break indicator */}
                              {index < sections.length - 1 && (
                                <div className="flex items-center gap-2 text-xs text-gray-300 mt-4">
                                  <div className="h-px w-8 bg-gray-400" />
                                  <span>Page Break</span>
                                  <div className="h-px w-8 bg-gray-400" />
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* Add section button */}
                        <button
                          onClick={() => addSection()}
                          className="w-64 py-3 bg-white border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-accent hover:text-accent transition-colors flex items-center justify-center gap-2 shadow-sm"
                        >
                          <Plus size={18} />
                          Add Page
                        </button>
                      </div>
                    ) : (
                      /* Two-Page Spread View - Uses percentage widths to fit container */
                      <div className="flex flex-col items-center gap-6 w-full">
                        {Array.from({ length: Math.ceil(sections.length / 2) }).map((_, pairIndex) => {
                          const leftSection = sections[pairIndex * 2];
                          const rightSection = sections[pairIndex * 2 + 1];
                          const pageAspectRatio = currentPageFormat.heightInches / currentPageFormat.widthInches;

                          return (
                            <div key={pairIndex} className="flex flex-col items-center gap-2 w-full">
                              {/* Section Headers - Above pages */}
                              <div className="flex gap-1 w-full">
                                <div className="flex-1">
                                  <SectionHeader
                                    section={leftSection}
                                    sectionNumber={pairIndex * 2 + 1}
                                    isFirst={pairIndex === 0}
                                    isLast={!rightSection && pairIndex === Math.ceil(sections.length / 2) - 1}
                                    onGenerate={() => generateSection(leftSection.id)}
                                    onMoveUp={() => handleMoveSection(leftSection.id, 'up')}
                                    onMoveDown={() => handleMoveSection(leftSection.id, 'down')}
                                    onAddAfter={() => addSection(leftSection.id)}
                                                                      />
                                </div>
                                <div className="flex-1">
                                  {rightSection ? (
                                    <SectionHeader
                                      section={rightSection}
                                      sectionNumber={pairIndex * 2 + 2}
                                      isFirst={false}
                                      isLast={pairIndex === Math.ceil(sections.length / 2) - 1}
                                      onGenerate={() => generateSection(rightSection.id)}
                                      onMoveUp={() => handleMoveSection(rightSection.id, 'up')}
                                      onMoveDown={() => handleMoveSection(rightSection.id, 'down')}
                                      onAddAfter={() => addSection(rightSection.id)}
                                                                          />
                                  ) : (
                                    <div className="h-full" />
                                  )}
                                </div>
                              </div>

                              {/* Two-Page Spread - Only content */}
                              <div className="flex gap-1 w-full">
                                {/* Left Page */}
                                <div
                                  className="flex-1 bg-white relative rounded-sm"
                                  style={{
                                    aspectRatio: `1 / ${pageAspectRatio}`,
                                    boxShadow: '-4px 4px 12px rgba(0, 0, 0, 0.15)',
                                  }}
                                >
                                  <LayoutRenderer
                                    layout={leftSection.layout || 'full-width'}
                                    sectionId={leftSection.id}
                                    images={imageBlocks[leftSection.id] || []}
                                    onImageEdit={() => handleAddImageToSection(leftSection.id)}
                                    content={
                                      <SectionContent
                                        section={leftSection}
                                        onRegenerate={(selectedText, startIndex, endIndex) =>
                                          openSelectiveRegenerate(leftSection.id, selectedText, startIndex, endIndex)
                                        }
                                      />
                                    }
                                  />
                                  <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 text-gray-400 text-[10px]">
                                    Page {pairIndex * 2 + 1}
                                  </div>
                                </div>

                                {/* Right Page */}
                                {rightSection ? (
                                  <div
                                    className="flex-1 bg-white relative rounded-sm"
                                    style={{
                                      aspectRatio: `1 / ${pageAspectRatio}`,
                                      boxShadow: '4px 4px 12px rgba(0, 0, 0, 0.15)',
                                    }}
                                  >
                                    <LayoutRenderer
                                      layout={rightSection.layout || 'full-width'}
                                      sectionId={rightSection.id}
                                      images={imageBlocks[rightSection.id] || []}
                                      onImageEdit={() => handleAddImageToSection(rightSection.id)}
                                      content={
                                        <SectionContent
                                          section={rightSection}
                                          onRegenerate={(selectedText, startIndex, endIndex) =>
                                            openSelectiveRegenerate(rightSection.id, selectedText, startIndex, endIndex)
                                          }
                                        />
                                      }
                                    />
                                    <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 text-gray-400 text-[10px]">
                                      Page {pairIndex * 2 + 2}
                                    </div>
                                  </div>
                                ) : (
                                  /* Empty placeholder for odd number of pages */
                                  <div
                                    className="flex-1 bg-gray-500/30 border-2 border-dashed border-gray-400 rounded flex items-center justify-center"
                                    style={{ aspectRatio: `1 / ${pageAspectRatio}` }}
                                  >
                                    <button
                                      onClick={() => addSection()}
                                      className="flex flex-col items-center gap-2 text-gray-300 hover:text-white transition-colors"
                                    >
                                      <Plus size={24} />
                                      <span className="text-xs font-medium">Add Page</span>
                                    </button>
                                  </div>
                                )}
                              </div>

                              {/* Page break indicator */}
                              {pairIndex < Math.ceil(sections.length / 2) - 1 && (
                                <div className="flex items-center gap-2 text-xs text-gray-300 mt-2">
                                  <div className="h-px w-6 bg-gray-400" />
                                  <span>Spread Break</span>
                                  <div className="h-px w-6 bg-gray-400" />
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* Add section button - only show if sections.length is even (no placeholder shown) */}
                        {sections.length % 2 === 0 && (
                          <button
                            onClick={() => addSection()}
                            className="w-48 py-2 bg-white border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-accent hover:text-accent transition-colors flex items-center justify-center gap-2 shadow-sm text-sm"
                          >
                            <Plus size={16} />
                            Add Page
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* Standard Scroll Mode - No document config */
                <div className="space-y-4">
                  {sections.map((section, index) => (
                    <div key={section.id}>
                      {/* Images before content */}
                      {imageBlocks[section.id]?.filter((b) => b.position === 'before').map((block) => (
                        <ImageBlock
                          key={block.id}
                          block={block}
                          sectionId={section.id}
                          onEdit={() => handleAddImageToSection(section.id)}
                        />
                      ))}

                      <SectionCard
                        section={section}
                        sectionNumber={index + 1}
                        isFirst={index === 0}
                        isLast={index === sections.length - 1}
                        onGenerate={() => generateSection(section.id)}
                        onRegenerate={(selectedText, startIndex, endIndex) =>
                          openSelectiveRegenerate(section.id, selectedText, startIndex, endIndex)
                        }
                        onMoveUp={() => handleMoveSection(section.id, 'up')}
                        onMoveDown={() => handleMoveSection(section.id, 'down')}
                        onAddAfter={() => addSection(section.id)}
                                              />

                      {/* Images after content */}
                      {imageBlocks[section.id]?.filter((b) => b.position === 'after').map((block) => (
                        <ImageBlock
                          key={block.id}
                          block={block}
                          sectionId={section.id}
                          onEdit={() => handleAddImageToSection(section.id)}
                        />
                      ))}
                    </div>
                  ))}

                  {/* Add section button */}
                  <button
                    onClick={() => addSection()}
                    className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-accent hover:text-accent transition-colors flex items-center justify-center gap-2"
                  >
                    <Plus size={18} />
                    Add Section
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Footer - Fixed at bottom of main area */}
          <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-20">
            <div>
              <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
                {/* Left: Section count & word count */}
                <div className="text-sm text-gray-500">
                  {sections.length} section{sections.length !== 1 ? 's' : ''}
                  {' | '}
                  {sections.reduce(
                    (acc, s) => acc + (s.content ? s.content.split(/\s+/).filter(Boolean).length : 0),
                    0
                  )}{' '}
                  words
                </div>

                {/* Right: Action buttons */}
                <div className="flex items-center gap-2">
                  {/* Generate All */}
                  <button
                    onClick={chainConfig.enabled ? chainGenerateAllSections : generateAllSections}
                    disabled={isGeneratingAll || isChainGenerating || sections.length === 0}
                    className="flex items-center gap-2 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                    title={chainConfig.enabled ? 'Generate all sections with chain mode' : 'Generate content for all sections'}
                  >
                    {isGeneratingAll || isChainGenerating ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        {chainConfig.enabled ? <Link2 size={16} /> : <Sparkles size={16} />}
                        {chainConfig.enabled ? 'Chain Generate' : 'Generate All'}
                      </>
                    )}
                  </button>

                  {/* Preview */}
                  <button
                    onClick={handlePreview}
                    disabled={sections.length === 0}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                    title="Preview document"
                  >
                    <Eye size={16} />
                    Preview
                  </button>

                  {/* Export PDF */}
                  <button
                    onClick={handleExportPDF}
                    disabled={isExportingPDF || sections.length === 0}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                    title="Export as PDF"
                  >
                    {isExportingPDF ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <FileDown size={16} />
                    )}
                    PDF
                  </button>

                  {/* Export DOCX */}
                  <button
                    onClick={handleExportDOCX}
                    disabled={isExportingDOCX || sections.length === 0}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                    title="Export as DOCX"
                  >
                    {isExportingDOCX ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <FileOutput size={16} />
                    )}
                    DOCX
                  </button>
                </div>
              </div>
            </div>
          </footer>
        </main>

        {/* Right Sidebar - Details/Documents/Settings */}
        <Sidebar
          onManageTemplates={() => setShowTemplateManager(true)}
          onManageDocumentTemplates={() => setShowGenericDocTemplateModal(true)}
        />
      </div>

      {/* Chat Widget (Floating) */}
      <ChatWidget />

      {/* Help Assistant Modal */}
      <HelpAssistant
        isOpen={showHelpAssistant}
        onClose={() => setShowHelpAssistant(false)}
      />

      {/* Export Modal */}
      {showExportModal && (
        <ExportModal onClose={() => setShowExportModal(false)} />
      )}

      {/* Document Preview Modal */}
      <DocumentPreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        onExportPDF={handleExportPDF}
        onExportDOCX={handleExportDOCX}
      />

      {/* Selective Regenerate Modal */}
      <SelectiveRegenerateModal
        isOpen={showSelectiveRegenModal}
        selectedText={selectiveRegenContext?.selectedText || ''}
        onRegenerate={handleSelectiveRegenerate}
        onClose={() => {
          setShowSelectiveRegenModal(false);
          setSelectiveRegenContext(null);
        }}
      />

      {/* Template Manager Modal */}
      <TemplateManagerModal
        isOpen={showTemplateManager}
        onClose={() => setShowTemplateManager(false)}
        templates={allTemplates}
        onSave={(updatedTemplates) => {
          setAllTemplates(updatedTemplates);
          setShowTemplateManager(false);
        }}
      />

      {/* Generic Document Template Modal */}
      <GenericDocumentTemplateModal
        isOpen={showGenericDocTemplateModal}
        onClose={() => setShowGenericDocTemplateModal(false)}
      />

      {/* Image Generation Modal */}
      <ImageGenerationModal
        isOpen={showImageGenerator}
        onClose={() => {
          setShowImageGenerator(false);
          setImageGenSectionId(null);
        }}
        onImageSelect={handleImageSelect}
      />

      {/* Cover Designer Modal */}
      <CoverDesigner
        isOpen={showCoverDesigner}
        onClose={() => setShowCoverDesigner(false)}
      />

      {/* Style Library Modal */}
      <StyleLibrary
        isOpen={showStyleLibrary}
        onClose={() => setShowStyleLibrary(false)}
      />

      {/* Publish Panel Modal */}
      <PublishPanel
        isOpen={showPublishPanel}
        onClose={() => setShowPublishPanel(false)}
      />

      {/* Project Setup Wizard */}
      <ProjectWizard
        isOpen={showProjectWizard}
        onClose={() => setShowProjectWizard(false)}
        onComplete={handleWizardComplete}
      />
    </div>
  );
}
